<template>
  <div
    :class="[
      $style.activePromoCard,
      {
        [$style.activePromoCardWager]: item.stage === 'handle_bets',
        [$style.activePromoCardDisabled]: isBonusDisabled,
      },
    ]"
  >
    <div :class="$style.activePromoCardImage">
      <div :class="$style.activePromoCardImageTop">
        <BitCardLabel
          v-if="!isBonusDisabled"
          :class="$style.activePromoCardLabel"
          :custom-color="itemLabelData.color"
        >
          {{ itemLabelData.label }}
        </BitCardLabel>
        <BitTimer
          v-if="itemTimerEndDate && new Date(itemTimerEndDate).getTime() - Date.now()"
          :class="$style.activePromoCardTimer"
          :start-time="Date.now()"
          :end-time="new Date(itemTimerEndDate).getTime()"
          :title-days="components.timer.daysTitle"
          :title-hours="components.timer.hoursTitle"
          :title-minutes="components.timer.minutesTitle"
          :title-seconds="components.timer.secondsTitle"
        />
      </div>
      <img :src="itemImage" alt="promo" />
    </div>
    <div :class="$style.activePromoCardContent">
      <div>
        <p :class="$style.activePromoCardTitle">{{ itemTitle }}</p>
        <p v-if="!isBonusDisabled" :class="$style.activePromoCardOffer">
          <span>{{ itemOfferText }}</span>
          <span v-if="'freespins_total' in item" :class="$style.activePromoCardOfferCurrency">
            ({{ item.currency }})
          </span>
        </p>
      </div>
      <template v-if="!isBonusDisabled">
        <template v-if="'freespins_total' in item && item.games_info">
          <ul
            v-if="item.games_info.length < 4"
            :class="[
              $style.activePromoCardGames,
              {
                [$style.onlyOneGameList]: item.games_info.length === 1,
                [$style.twoGameList]: item.games_info.length === 2,
              },
            ]"
          >
            <li v-for="game in item.games_info" :key="game.identifier">
              <NuxtLink
                :to="
                  getGameUrl({
                    id: game.identifier,
                    title: game.title,
                  })
                "
              >
                {{ game.title }}
              </NuxtLink>
            </li>
          </ul>
          <BitLinkButton
            v-else
            :class="$style.activePromoCardAllGamesBtn"
            @click.prevent="openFreespinGamesModal"
          >
            {{ bonusesTextData.bonusGamesTitle }}
            <template #prependIcon>
              <GamesCatalogueNavIcon />
            </template>
          </BitLinkButton>
        </template>

        <ProgressBar
          v-if="item.stage === 'handle_bets'"
          :class="$style.activePromoCardProgress"
          :right-title="wagerRightTitle"
          :left-title="wagerLeftTitle"
          :progress="`${Math.floor(
            (item.amount_wager_cents / item.amount_wager_requirement_cents) * 100
          )}%`"
        />
        <div :class="$style.activePromoCardButtons">
          <BitButton
            v-if="item.cancelable"
            :disabled="isWaitingActivation || isWaitingCancel"
            :loading="isWaitingCancel"
            type="secondary"
            @click="handleBonusCancel"
          >
            {{ forfeitButton }}
          </BitButton>
          <BitButton
            v-if="item.activatable"
            type="secondary"
            :loading="isWaitingActivation"
            :disabled="isWaitingActivation || isWaitingCancel"
            @click="handleBonusActivate"
          >
            {{ storeButtons.activate }}
          </BitButton>
          <BitButton
            v-if="strapiBonusData && !item.activatable"
            type="secondary"
            @click="openBonusInfoModal"
          >
            {{ bonusesTextData.bonusInfoBtn }}
          </BitButton>
        </div>
      </template>
      <template v-if="isBonusDisabled">
        <div :class="$style.activePromoCardDisabledBlock">
          <div :class="$style.activePromoCardDisabledBlockTitle">
            <LockedIcon />
            <span>{{ bonusesTextData.lockedBonusTitle }}</span>
          </div>
          <p
            :class="$style.activePromoCardDisabledBlockText"
            v-html="bonusesTextData.lockedBonusText"
          />
        </div>
        <BitTimer
          v-if="itemTimerEndDate && new Date(itemTimerEndDate).getTime() - Date.now()"
          :class="[$style.activePromoCardTimer, $style.activePromoCardTimerDisabled]"
          :start-time="Date.now()"
          :end-time="new Date(itemTimerEndDate).getTime()"
          :is-background="false"
          :title-days="components.timer.daysTitle"
          :title-hours="components.timer.hoursTitle"
          :title-minutes="components.timer.minutesTitle"
          :title-seconds="components.timer.secondsTitle"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitCardLabel, BitTimer, BitButton, BitLinkButton } from '@bit/ui-kit';
import ProgressBar from '~/components/lib/progressBar/progressBar.vue';
import LockedIcon from '~/public/images/common/ico_lock_bonus.svg?component';
import GamesCatalogueNavIcon from '~/public/images/common/ico_slider_arrow_right.svg?component';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreSsPlayerBonuses } from '~/stores/softswiss/playerBonuses';
import { useStoreBonuses } from '~/stores/strapi/bonuses';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreRaces } from '~/stores/strapi/races';
import { EModalName } from '~/types/modals';
import { capitalize } from '~/utils/date/helpers';
import { formatAmmount } from '~/utils/notifications/ammountFormating';
import type { ISsPlayerBonuses, ISsPlayerFreespins } from '~/types/softswiss/ssPlayerBonuses';
interface activePromotionCardProps {
  item: ISsPlayerBonuses | ISsPlayerFreespins;
}

const props = defineProps<activePromotionCardProps>();

const components = useStoreComponents();
const storeModals = useStoreModals();
const storeButtons = useStoreButtonTitles();
const storeBonuses = useStoreBonuses();
const playerSsBonuses = useStoreSsPlayerBonuses();
const storeRaces = useStoreRaces();

const { getGameUrl } = useUrlConstructor();

const isWaitingActivation = ref(false);
const isWaitingCancel = ref(false);

const bonusesTextData = computed(() => components.bonuses);

const forfeitButton = computed(() =>
  props.item.stage === 'issued' ? storeButtons.forfeit : storeButtons.cancel
);

const strapiBonusData = computed(() => {
  return (
    storeBonuses.allBonusesWithStatuses.find(
      (bonus) =>
        bonus.bonusTitleForBO === props.item.title ||
        props.item.title.includes(bonus.bonusTitleForBO)
    ) || null
  );
});
const strapiRaceData = computed(() => {
  return (
    storeRaces.races.find(
      (race) =>
        race.bonusTitleForBO === props.item.title || props.item.title.includes(race.bonusTitleForBO)
    ) || null
  );
});

const itemTitle = computed(() => {
  return props.item.title.replace('WOF', 'Wheel of Fortune:');
});

const itemImage = computed(() => {
  const customImage = components.bonusCustomImages?.find(
    (item) => item.bonusTitle === props.item.title
  );
  if (customImage) {
    if (customImage) {
      return customImage.image.url;
    }
  } else if (strapiBonusData.value) {
    return strapiBonusData.value.bannerCardFront.url;
  } else if (strapiRaceData.value) {
    return strapiRaceData.value.bannerCardFront.url;
  } else if ('freespins_total' in props.item) {
    return '/images/common/PromoActiveBonusesCash.webp';
  } else if (props.item.strategy === 'exchange') {
    return '/images/common/PromoActiveBonusesCompoints.webp';
  }
  return '/images/common/PromoActiveBonusesFS.webp';
});

const wagerLeftTitle = computed(() => {
  if (props.item.stage === 'handle_bets') {
    return `Wager x${props.item.amount_wager_requirement_cents / props.item.amount_cents}`;
  }
  return '';
});
const wagerRightTitle = computed(() => {
  if (props.item.stage === 'handle_bets') {
    const currentAmount = formatAmmount(props.item.amount_wager_cents, props.item.currency).split(
      ' '
    )[0];
    return `${currentAmount}/${formatAmmount(
      props.item.amount_wager_requirement_cents,
      props.item.currency
    )}`;
  }
  return '';
});

const isBonusDisabled = computed(() => {
  if (!playerSsBonuses.activeBonusesAndFreespins.length || props.item.stage !== 'issued') {
    return false;
  }
  const firstActiveBonus = [...playerSsBonuses.activeBonusesAndFreespins].reverse()[0];
  if (firstActiveBonus.id === props.item.id) {
    return false;
  }
  return playerSsBonuses.activeBonusesAndFreespins.every(
    (bonus) => bonus.title !== props.item.title
  );
});

const itemTimerEndDate = computed(() => {
  return props.item.activatable ? props.item.activatable_until : props.item.valid_until;
});

const itemOfferText = computed(() => {
  if ('freespins_total' in props.item) {
    return props.item.freespins_total + ' FS';
  } else {
    return formatAmmount(props.item.amount_cents, props.item.currency);
  }
});

const itemLabelData = computed(() => {
  if (props.item.stage === 'issued') {
    return {
      label: bonusesTextData.value.labelIssued || capitalize(props.item.stage),
      color: 'yellow',
    };
  }
  return {
    label: bonusesTextData.value.labelActivated || capitalize(props.item.stage),
    color: 'blue',
  };
});

const openFreespinGamesModal = () => {
  storeModals.openModal(EModalName.freespin_games, {
    closeAll: true,
    freespinId: props.item.id,
  });
};
const openBonusInfoModal = () => {
  storeModals.openModal(EModalName.bonus_promo, {
    closeAll: true,
    bonusId: strapiBonusData.value?.bonusId,
  });
};
const handleBonusActivate = async () => {
  if ('freespins_total' in props.item) {
    isWaitingActivation.value = true;
    await playerSsBonuses.activateFreespins(props.item.id);
    await playerSsBonuses.freespinActivationCheck({
      freespinId: props.item.id,
    });
    isWaitingActivation.value = false;
  } else {
    isWaitingActivation.value = true;
    await playerSsBonuses.activateBonuses(props.item.id);
    await playerSsBonuses.bonusActivationCheck({
      bonusId: props.item.id,
    });
    isWaitingActivation.value = false;
  }
};
const handleBonusCancel = () => {
  storeModals.openModal(EModalName.before_cancel_bonus, {
    closeAll: true,
    bonusId: props.item.id,
  });
};
</script>

<style lang="scss" module src="./activePromotionCard.scss"></style>
